import {
  Box,
  Button,
  ButtonProps,
  HStack,
  Heading,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Radio,
  RadioGroup,
  Text,
  VStack
} from "@chakra-ui/react"
import { ChevronRightIcon, SparklesIcon } from "@pathwright/pathicons"
import HTMLText from "@pathwright/ux/src/components/text/HTMLText"
import { onMeasureType } from "@pathwright/ux/src/components/text/TruncatedHTML"
import { Fragment, useMemo, useState } from "react"
import { Link as ReactRouterLink } from "react-router-dom"
import { OfferAccessState } from "../api/generated"
import CoverPageDivider from "./CoverPageDivider"
import { ImmersiveTriggerButton } from "./CoverPageImmersive"
import { useCoverPageContext } from "./CoverPageProvider"
import { type CoverPageOffer, type CoverPageResource } from "./types"

const tPrefixForm = "resource.offer_form"
const tPrefixInfo = "resource.offer_info"

const dividerHeight = 21 // CoverPageDivider height

const getRegisterURL = (offer: CoverPageOffer, resourceSlug: string) => {
  return `/${resourceSlug}/${offer.id}/cover/register/`
}

export const offerButtonProps: ButtonProps = {
  colorScheme: "brand",
  textDecoration: "none",
  fontWeight: "700",
  size: "lg",
  _hover: { textDecoration: "none", opacity: 0.9 }
}

export const OfferButton = ({
  offer,
  selectedOptionIndex,
  buttonProps: buttonPropsOverride,
  renderButton
}: {
  offer: CoverPageOffer
  selectedOptionIndex: number
  buttonProps?: ButtonProps
  renderButton?: (props: ButtonProps) => React.ReactNode
}) => {
  const { resource } = useCoverPageContext()
  const selectedOption = offer.price_options[selectedOptionIndex]
  const hasAccess = offer.access.state === OfferAccessState.Active
  const isUnavailable = offer.access.state === OfferAccessState.Unavailable

  const buttonProps: any = useMemo(() => {
    const props: any = {
      children: selectedOption.button_label || "Continue",
      w: "max-content",
      ...offerButtonProps,
      ...buttonPropsOverride
    }

    if (hasAccess) {
      props.as = ReactRouterLink
      props.to = `/library/${resource.slug}/${offer.id}/path/`
      props.rightIcon = <ChevronRightIcon />
    } else if (isUnavailable) {
      props.isDisabled = true
    } else {
      // Default to registration for any other state (e.g. available)
      props.as = ReactRouterLink
      props.to = getRegisterURL(offer, resource.slug)
    }

    // Prevent the card wrapper's onClick (which opens the offer immersive route)
    // from hijacking this button's navigation to /register.
    props.onClick = (e: React.MouseEvent) => {
      e.stopPropagation()
    }

    return props
  }, [
    hasAccess,
    isUnavailable,
    offer.id,
    resource.slug,
    selectedOption.button_label
  ])

  return renderButton ? renderButton(buttonProps) : <Button {...buttonProps} />
}

type OfferPricingProps = {
  offer: CoverPageOffer
  resource: CoverPageResource
}

const OfferPricing = ({ offer, resource }: OfferPricingProps) => {
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(0)

  const handleChange = (idx: number) => {
    setSelectedOptionIndex(idx)
  }

  return (
    <VStack gap="5px" align="center">
      <OfferButton offer={offer} selectedOptionIndex={selectedOptionIndex} />
      <OfferPriceDescription
        offer={offer}
        selectedOptionIndex={selectedOptionIndex}
        onChangeSelectedOptionIndex={handleChange}
      />
    </VStack>
  )
}

const OfferPriceDescription = ({
  offer,
  selectedOptionIndex,
  onChangeSelectedOptionIndex
}: {
  offer: CoverPageOffer
  selectedOptionIndex: number
  onChangeSelectedOptionIndex: (index: number) => void
}) => {
  const selectedOption = offer.price_options[selectedOptionIndex]
  const hasMultipleOptions = (offer.price_options.length || 0) > 1

  if (hasMultipleOptions) {
    return (
      <RadioGroup
        value={selectedOptionIndex.toString()}
        onChange={(val) => onChangeSelectedOptionIndex(Number(val))}
      >
        <HStack
          spacing={2}
          align="stretch"
          sx={{
            ".chakra-radio__control": {
              height: "1rem !important",
              width: "1rem !important"
            },
            ".chakra-radio__label": {
              marginLeft: "3px"
            }
          }}
        >
          {offer.price_options.map((option, index) => (
            <Radio key={index} value={index.toString()} size="sm">
              {option.option_label || option.button_label}
            </Radio>
          ))}
        </HStack>
      </RadioGroup>
    )
  }

  if (selectedOption.price_description) {
    return (
      <Box
        m={0}
        noOfLines={1}
        as="span"
        fontSize="xs"
        color="gray.500"
        lineHeight="1 !important"
        dangerouslySetInnerHTML={{
          __html: selectedOption.price_description
        }}
      />
    )
  }
  return null
}
// OfferBody: Renders the offer description with overflow detection
const OfferBody = ({
  offer,
  maxHeight,
  onMeasureBody,
  forceBodyHeight
}: {
  offer: CoverPageOffer
  maxHeight?: number
  onMeasureBody?: onMeasureType
  forceBodyHeight?: number
}) => {
  // if (!offer.description) return null

  const effectiveHeight = forceBodyHeight ?? maxHeight
  const truncate = forceBodyHeight == null

  return (
    <Box
      position="relative"
      h={effectiveHeight ? `${effectiveHeight}px` : "100%"}
      maxH="100%"
      overflow={"hidden"}
      minH={effectiveHeight ? `${effectiveHeight}px` : "0"}
    >
      <HTMLText
        html={offer.description}
        truncate={truncate}
        truncateProps={
          truncate
            ? {
                maxHeight,
                // ellipsis: true,
                onMeasure: onMeasureBody,
                renderMore: () => (
                  <ImmersiveTriggerButton
                    route={{ name: "offer", id: offer.id }}
                  />
                )
              }
            : undefined
        }
      />
    </Box>
  )
}

// OfferMeta: Renders the access meta information
const OfferMeta = ({ offer }: { offer: CoverPageOffer }) => {
  if (offer.access.meta.length === 0) return null

  return (
    <HStack
      spacing={1}
      align="stretch"
      justify="flex-start"
      alignItems="center"
      whiteSpace="nowrap"
      overflow="hidden"
      textOverflow="ellipsis"
      maxW="100%"
      lineHeight="1"
    >
      {offer.access.meta.map((meta, index) => (
        <Fragment key={`offer-meta-${index}`}>
          {index > 0 && (
            <Text
              as="span"
              color="gray.400"
              fontSize="md"
              mx={0}
              lineHeight="1 !important"
              mt={"-3px"}
            >
              &bull;
            </Text>
          )}
          <Text
            key={index}
            fontSize="xs"
            color="gray.500"
            m={0}
            w="max-content"
            as="span"
            lineHeight="1 !important"
          >
            {meta}
          </Text>
        </Fragment>
      ))}
    </HStack>
  )
}

export const InspectOffering = ({ offer }: { offer: CoverPageOffer }) => {
  return (
    <Popover placement="right-end">
      <PopoverTrigger>
        <IconButton
          aria-label="Inspect Offering"
          className="inspect-offering-trigger"
          icon={<SparklesIcon size="0.9rem" />}
          size="sm"
          variant="ghost"
          position="absolute"
          top={4}
          right={4}
          zIndex={2}
          cursor="pointer"
          opacity={0}
          transition="opacity 0.2s, pointer-events 0.2s"
        />
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          bg="gray.100"
          width="xl"
          top={"5vh"}
          maxW="90vw"
          maxH="90vh"
          overflow="auto"
          p={6}
        >
          <PopoverArrow />
          <PopoverBody>
            <Text fontSize="sm" fontWeight="bold" mb={2} fontFamily="mono">
              Offering Data
            </Text>
            <Box
              as="pre"
              fontSize="xs"
              fontFamily="mono"
              whiteSpace="pre-wrap"
              m={0}
            >
              {JSON.stringify(offer, null, 2)}
            </Box>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  )
}

// Main offer card component (default export, keep as CoverPageOffer)
type CoverPageOfferCardProps = {
  offer: CoverPageOffer
  showInspectOffering?: boolean
  displayMeta?: boolean
  displayDescription?: boolean
  maxCardHeight?: number // must be a number as it's used in computations
  onMeasureBody?: onMeasureType
  forceBodyHeight?: number
}

const CoverPageOfferCard = ({
  offer,
  showInspectOffering,
  displayMeta,
  displayDescription,
  maxCardHeight,
  onMeasureBody,
  forceBodyHeight
}: CoverPageOfferCardProps) => {
  const { resource, onOpenImmersive, immersiveActive } = useCoverPageContext()

  const handleOpenImmersive = (e: React.MouseEvent<HTMLDivElement>) => {
    if (immersiveActive) return

    onOpenImmersive?.({ name: "offer", id: offer.id })
  }

  // Selection handling is encapsulated in CoverPageOfferPricing

  // Dynamic height calculations based on all cards (all in pixels)
  const titleHeight = 19.55 // 1.15rem * 17px
  const headingSpacer = 8 // 0.5rem * 16px
  const priceDescriptionHeight = displayDescription ? 12 : 0 // 0.75rem * 16px (sm)
  const pricingSpacer = displayDescription ? 5 : 0 // gap
  const metaHeight = displayMeta ? 12 : 0 // 0.75rem * 16px (sm)
  const metaSpacer = displayMeta ? 5 : 0 // gap
  const buttonHeight = 47.59 // Button height

  // Calculate bottom section height (divider and pricing)
  const bottomSectionHeight =
    dividerHeight +
    buttonHeight +
    priceDescriptionHeight +
    pricingSpacer +
    metaHeight +
    metaSpacer

  // Calculate available height for body content (accounting for title at top)
  const titleSectionHeight = titleHeight + headingSpacer
  const allPadding = 64 // 2 x Chakra padding 4 (32px) = 64px
  const availableBodyHeight = maxCardHeight
    ? Math.max(
        0,
        maxCardHeight - titleSectionHeight - bottomSectionHeight - allPadding
      )
    : undefined

  if (!resource) return null

  return (
    <Box
      className="CoverPageOfferCardWrapper"
      p={4} // show the box shadow inside the parent item
      cursor={immersiveActive ? "default" : "pointer"}
      onClick={handleOpenImmersive}
    >
      <VStack
        spacing={0}
        className="CoverPageOfferCard"
        align="stretch"
        borderRadius="card"
        bg="white"
        p={4} // content padding
        maxH={maxCardHeight ? `${maxCardHeight}px` : "100%"}
        h={"100%"}
        shadow="card"
        _hover={{
          shadow: "cardHover",
          ".inspect-offering-trigger": { opacity: 1 }
        }}
        transition="all 0.2s"
        position="relative"
      >
        {offer && showInspectOffering && <InspectOffering offer={offer} />}

        {/* Title */}
        <Heading
          noOfLines={1}
          as="h4"
          size="h7"
          mt={0}
          mb={"8px"}
          className="theme-heading theme-heading-semibold"
          flexShrink={0}
        >
          {offer.title}
        </Heading>

        {/* Body - fills available space between title and bottom section */}
        <Box
          className="OfferBodyWrapper"
          top={`${titleSectionHeight}px`}
          left="1rem"
          right="1rem"
          bottom={`${bottomSectionHeight}px`}
          overflow="hidden"
          cursor={"text"}
          onClick={(event) => {
            event.preventDefault()
            event.stopPropagation()
          }}
          h={availableBodyHeight ? `${availableBodyHeight}px` : "100%"}
          // h="max-content"
        >
          <OfferBody
            offer={offer}
            maxHeight={availableBodyHeight ?? undefined}
            onMeasureBody={onMeasureBody}
            forceBodyHeight={forceBodyHeight}
          />
        </Box>

        {/* Divider */}
        <Box flexShrink={0} h={`${dividerHeight}px`}>
          <CoverPageDivider verticalMargin="10px" horizontalMargin="0px" />
        </Box>

        {/* Bottom section - consistent height across all cards */}
        <VStack
          flexShrink={0}
          align="center"
          gap={"5px"}
          h={`${
            buttonHeight +
            priceDescriptionHeight +
            pricingSpacer +
            metaHeight +
            metaSpacer
          }px`}
          justify="flex-start"
        >
          <OfferPricing offer={offer} resource={resource} />
          {displayMeta && <OfferMeta offer={offer} />}
        </VStack>
      </VStack>
    </Box>
  )
}

export const CoverPageOfferImmersive = ({ id }: { id?: number }) => {
  const { offers } = useCoverPageContext()

  if (!offers?.length) return null

  let offer = offers?.find((o) => Number(o.id) === Number(id))
  if (!offer) offer = offers[0]

  return (
    <Box
      w="100%"
      h="100%"
      pos="relative"
      px={{ base: 0, sm: "20px", md: "40px" }}
      maxW="1000px"
      mx="auto"
    >
      <CoverPageOfferCard
        offer={offer}
        displayMeta={true}
        displayDescription={true}
      />
    </Box>
  )
}

export default CoverPageOfferCard
